import { useEffect, useState } from 'react';
import he from 'he';
import { parseWordpressResponseJson } from '@/utils/parseWordpressJson';

interface WordPressRenderedField {
  rendered?: string;
}

interface WordPressWhitepaper {
  id: number;
  date: string;
  link: string;
  slug: string;
  title?: WordPressRenderedField;
  excerpt?: WordPressRenderedField;
  content?: WordPressRenderedField;
  acf?: {
    pdf_file?: string;
    short_description?: string;
    author_name?: string;
    website?: string;
    pages_number?: string | number;
    reading_time?: string | number;
  };
  meta?: {
    pdf_url?: string;
    website?: string;
  };
  pdf_url?: string;
  whitepaper_pdf_url?: string;
  website?: string;
  pages_number?: string | number;
  reading_time?: string | number;
}

export interface WhitepaperCardItem {
  id: number;
  slug: string;
  title: string;
  description: string;
  descriptionHtml: string;
  link: string;
  date: string;
  pdfUrl: string | null;
  pagesNumber: number | null;
  readingTime: string | null;
}

const WHITEPAPERS_ENDPOINTS = ['https://blog.medbioinformatics.com/wp-json/wp/v2/whitepaper'];

const stripHtml = (value: string) =>
  value
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
const extractPdfUrl = (html: string) => {
  const match = html.match(/https:\/\/[^\s"'<>]+\.pdf(?:\?[^\s"'<>]*)?/i);
  return match?.[0] || null;
};
const getValidHttpsUrl = (value?: string | null) => {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'https:') return null;
    if (!parsed.hostname.includes('.')) return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const parsePagesNumber = (value: string | number | null | undefined) => {
  if (value == null || value === '') return null;
  const parsed = Number(String(value).trim());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const formatReadingTime = (value: string | number | null | undefined) => {
  if (value == null || value === '') return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/min/i.test(trimmed)) return trimmed;
  const minutes = Number(trimmed);
  if (Number.isFinite(minutes) && minutes > 0) return `${minutes} min`;
  return trimmed;
};

const isDisgenetWhitepaper = (item: WordPressWhitepaper) => {
  const website = String(item.website || item.acf?.website || item.meta?.website || '')
    .trim()
    .toLowerCase();
  return website === 'disgenet';
};

export const useWhitepapers = () => {
  const [whitepapers, setWhitepapers] = useState<WhitepaperCardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWhitepapers = async () => {
      let data: WordPressWhitepaper[] | null = null;

      for (const endpoint of WHITEPAPERS_ENDPOINTS) {
        try {
          const response = await fetch(endpoint);
          if (!response.ok) {
            continue;
          }
          const parsed = await parseWordpressResponseJson<WordPressWhitepaper[]>(response);
          if (Array.isArray(parsed)) {
            data = parsed;
            break;
          }
        } catch {
          // Try next endpoint
        }
      }

      if (!data) {
        throw new Error('Whitepapers request failed for all configured endpoints');
      }

      {
        const mappedWhitepapers = data.filter(isDisgenetWhitepaper).map(item => {
          const decodedTitle = he.decode(item.title?.rendered || 'Untitled whitepaper');
          const rawDescriptionHtml = item.content?.rendered || item.excerpt?.rendered || '';
          const rawDescription =
            item.acf?.short_description || item.excerpt?.rendered || item.content?.rendered || '';
          const decodedDescription = he.decode(stripHtml(rawDescription));
          const shortDescription =
            decodedDescription.length > 200 ? `${decodedDescription.slice(0, 200)}...` : decodedDescription;
          const explicitPdfUrl =
            getValidHttpsUrl(item.whitepaper_pdf_url) ||
            getValidHttpsUrl(item.pdf_url) ||
            getValidHttpsUrl(item.acf?.pdf_file) ||
            getValidHttpsUrl(item.meta?.pdf_url);
          const pdfUrl = explicitPdfUrl || extractPdfUrl(item.content?.rendered || '');
          const pagesNumber = parsePagesNumber(item.pages_number ?? item.acf?.pages_number);
          const readingTime = formatReadingTime(item.reading_time ?? item.acf?.reading_time);

          return {
            id: item.id,
            slug: item.slug,
            title: decodedTitle,
            description: shortDescription,
            descriptionHtml: he.decode(rawDescriptionHtml),
            link: item.link,
            date: formatDate(item.date),
            pdfUrl,
            pagesNumber,
            readingTime,
          };
        });

        setWhitepapers(mappedWhitepapers);
        setLoading(false);
      }
    };

    fetchWhitepapers().catch(fetchError => {
      setError(fetchError?.message || 'Failed to load whitepapers');
      setLoading(false);
    });
  }, []);

  return { whitepapers, loading, error };
};
